import { useCallback, useEffect, useRef, useState } from 'react';
import { apiDelete, apiGet, apiPost, ApiError } from '../api/client';
import type { Snippet, SnippetsListResponse, SnippetsUpsertResponse } from '../api/types';
import { normalizeTrigger, truncate } from '../lib/format';
import Icon from '../components/Icon';
import TopAppBar, { AccountActionButton } from '../components/TopAppBar';

const MAX_TRIGGER_LENGTH = 32;
const MAX_EXPANSION_LENGTH = 2048;

type LoadState = 'idle' | 'loading' | 'error' | 'ready';

interface FormState {
  trigger: string;
  expansion: string;
}

interface FormError {
  trigger?: string;
  expansion?: string;
}

const EMPTY_FORM: FormState = { trigger: '', expansion: '' };

/** Validate a create/edit form. Returns field-keyed errors or {} when valid. */
function validateForm(form: FormState): FormError {
  const errors: FormError = {};
  const t = form.trigger.trim();
  const e = form.expansion.trim();
  if (!t) errors.trigger = 'Trigger is required.';
  else if (t.length > MAX_TRIGGER_LENGTH)
    errors.trigger = `Trigger must be ≤ ${MAX_TRIGGER_LENGTH} chars.`;
  if (!e) errors.expansion = 'Expansion is required.';
  else if (e.length > MAX_EXPANSION_LENGTH)
    errors.expansion = `Expansion must be ≤ ${MAX_EXPANSION_LENGTH} chars.`;
  return errors;
}

/** Derive a display title from the expansion's first non-empty line. */
function snippetTitle(s: Snippet): string {
  const firstLine = s.expansion
    .split('\n')
    .map((l) => l.trim())
    .find((l) => l.length > 0);
  return firstLine ? truncate(firstLine, 48) : s.trigger;
}

const INPUT_BASE =
  'w-full rounded-lg bg-surface px-3.5 text-on-surface font-body-md border border-outline-variant/60 focus:border-primary focus:outline-none focus:ring-0 transition-colors placeholder:text-on-surface-variant';

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
}

/**
 * Snippets view — trigger→expansion pairs that sync to the keyboard.
 *
 * Create and edit both POST a single-element array (the server upserts
 * with last-write-wins on `updated_at`). Delete soft-deletes via
 * `DELETE /snippets/:id`; the tombstone propagates to other devices.
 */
export default function SnippetsView() {
  const [snippets, setSnippets] = useState<Snippet[]>([]);
  const [state, setState] = useState<LoadState>('idle');
  const [error, setError] = useState<string | null>(null);

  // Create form.
  const [createForm, setCreateForm] = useState<FormState>(EMPTY_FORM);
  const [createErrors, setCreateErrors] = useState<FormError>({});
  const [saving, setSaving] = useState(false);

  // Inline edit + delete confirm keyed by snippet_id.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<FormState>(EMPTY_FORM);
  const [editErrors, setEditErrors] = useState<FormError>({});
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Card hover menu + client-side search.
  const [menuId, setMenuId] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  const createTriggerRef = useRef<HTMLInputElement>(null);
  const menuTriggerRefs = useRef(new Map<string, HTMLButtonElement>());
  const deleteTriggerRef = useRef<HTMLButtonElement | null>(null);
  const deleteDialogRef = useRef<HTMLDivElement | null>(null);
  const deleteCancelRef = useRef<HTMLButtonElement | null>(null);
  const deleteDialogWasOpenRef = useRef(false);

  const load = useCallback(() => {
    setState('loading');
    setError(null);
    apiGet<SnippetsListResponse>('/snippets')
      .then((res) => {
        setSnippets(res.snippets);
        setState('ready');
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Failed to load snippets');
        setState('error');
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const restoreMenuFocus = useCallback((id: string) => {
    requestAnimationFrame(() => menuTriggerRefs.current.get(id)?.focus());
  }, []);

  const closeMenu = useCallback(() => {
    if (!menuId) return;
    const id = menuId;
    setMenuId(null);
    restoreMenuFocus(id);
  }, [menuId, restoreMenuFocus]);

  useEffect(() => {
    if (!menuId) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      closeMenu();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [menuId, closeMenu]);

  useEffect(() => {
    if (deleteId) {
      deleteDialogWasOpenRef.current = true;
      requestAnimationFrame(() => deleteCancelRef.current?.focus());
      return;
    }
    if (deleteDialogWasOpenRef.current) {
      deleteDialogWasOpenRef.current = false;
      requestAnimationFrame(() => deleteTriggerRef.current?.focus());
    }
  }, [deleteId]);

  useEffect(() => {
    if (!deleteId) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (saving) return;
        event.preventDefault();
        setDeleteId(null);
        return;
      }
      if (event.key !== 'Tab') return;

      const dialog = deleteDialogRef.current;
      if (!dialog) return;
      const focusable = focusableElements(dialog);
      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !dialog.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !dialog.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [deleteId, saving]);

  const handleCreate = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const errors = validateForm(createForm);
      setCreateErrors(errors);
      if (Object.keys(errors).length > 0) return;

      setSaving(true);
      setError(null);
      const now = Date.now();
      const payload = {
        snippets: [
          {
            snippet_id: crypto.randomUUID(),
            trigger: createForm.trigger.trim(),
            trigger_normalized: normalizeTrigger(createForm.trigger),
            expansion: createForm.expansion.trim(),
            updated_at: now,
          },
        ],
      };
      apiPost<SnippetsUpsertResponse>('/snippets', payload)
        .then((res) => {
          const skipped = res.skipped?.[0];
          if (skipped) {
            const msg =
              skipped.reason === 'trigger_collision'
                ? 'A snippet with this trigger already exists.'
                : `Could not save snippet (${skipped.reason}).`;
            setCreateErrors({ trigger: msg });
            return;
          }
          setCreateForm(EMPTY_FORM);
          setCreateErrors({});
          load();
        })
        .catch((err: unknown) => {
          setError(err instanceof ApiError ? err.message : 'Failed to create snippet');
          setState('error');
        })
        .finally(() => setSaving(false));
    },
    [createForm, load],
  );

  const startEdit = useCallback((s: Snippet) => {
    setEditingId(s.snippet_id);
    setEditForm({ trigger: s.trigger, expansion: s.expansion });
    setEditErrors({});
    setMenuId(null);
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setEditForm(EMPTY_FORM);
    setEditErrors({});
  }, []);

  const saveEdit = useCallback(
    (s: Snippet) => {
      const errors = validateForm(editForm);
      setEditErrors(errors);
      if (Object.keys(errors).length > 0) return;

      setSaving(true);
      setError(null);
      const now = Date.now();
      const payload = {
        snippets: [
          {
            snippet_id: s.snippet_id,
            trigger: editForm.trigger.trim(),
            trigger_normalized: normalizeTrigger(editForm.trigger),
            expansion: editForm.expansion.trim(),
            updated_at: now,
          },
        ],
      };
      apiPost<SnippetsUpsertResponse>('/snippets', payload)
        .then((res) => {
          const skipped = res.skipped?.find((row) => row.snippet_id === s.snippet_id);
          if (skipped) {
            const msg =
              skipped.reason === 'trigger_collision'
                ? 'A snippet with this trigger already exists.'
                : `Could not save snippet (${skipped.reason}).`;
            setEditErrors({ trigger: msg });
            return;
          }
          cancelEdit();
          load();
        })
        .catch((err: unknown) => {
          setError(err instanceof ApiError ? err.message : 'Failed to save snippet');
          setState('error');
        })
        .finally(() => setSaving(false));
    },
    [editForm, cancelEdit, load],
  );

  const confirmDelete = useCallback(() => {
    if (!deleteId) return;
    setSaving(true);
    apiDelete<{ deleted: number }>(`/snippets/${encodeURIComponent(deleteId)}`)
      .then(() => {
        setDeleteId(null);
        if (editingId === deleteId) cancelEdit();
        load();
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Failed to delete snippet');
        setState('error');
      })
      .finally(() => setSaving(false));
  }, [deleteId, editingId, cancelEdit, load]);

  const focusCreate = useCallback(() => {
    cancelEdit();
    setCreateForm(EMPTY_FORM);
    setCreateErrors({});
    requestAnimationFrame(() => createTriggerRef.current?.focus());
  }, [cancelEdit]);

  const editingSnippet = editingId
    ? snippets.find((s) => s.snippet_id === editingId) ?? null
    : null;

  const filtered = query.trim()
    ? snippets.filter((s) => {
        const q = query.toLowerCase();
        return (
          s.trigger.toLowerCase().includes(q) || s.expansion.toLowerCase().includes(q)
        );
      })
    : snippets;

  const isEditing = editingSnippet !== null;

  return (
    <>
      <TopAppBar
        title="Snippets"
        actions={<AccountActionButton />}
      />

      <div className="mx-auto w-full max-w-max-width-content px-gutter pb-24 pt-24">
        <div className="flex min-h-0 flex-col gap-8 lg:h-[calc(100dvh-128px)] lg:flex-row lg:gap-10">
          {/* Snippets List Area */}
          <div className="flex min-w-0 flex-1 flex-col">
            {/* List Header */}
            <div className="mb-6 flex shrink-0 flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="mb-1 font-label-sm text-label-sm uppercase tracking-[0.12em] text-primary">
                  Keyboard library
                </p>
                <h2 className="font-headline-lg text-headline-lg text-on-surface">Saved text expansions</h2>
                <p className="mt-1 max-w-xl font-body-sm text-body-sm text-on-surface-variant">
                  Manage the trigger phrases that expand in the Loaf keyboard.
                </p>
              </div>
              <div className="flex w-full items-center gap-3 sm:w-auto">
                <div className="relative min-w-0 flex-1 sm:flex-none">
                  <Icon
                    name="search"
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant"
                  />
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    aria-label="Search snippets"
                    placeholder="Search snippets..."
                    className="h-11 w-full rounded-lg border border-outline-variant/60 bg-surface-container pl-11 pr-3 font-body-md text-body-md text-on-surface transition-colors placeholder:text-on-surface-variant focus:border-primary focus:outline-none focus:ring-0 sm:w-56"
                  />
                </div>
                <button
                  type="button"
                  onClick={focusCreate}
                  className="flex h-11 shrink-0 items-center gap-2 rounded-lg bg-primary px-4 font-label-lg text-label-lg font-medium text-on-primary transition-colors hover:bg-primary/90 active:scale-95 active:duration-200 sm:px-5"
                >
                  <Icon name="add" className="text-[20px]" />
                  <span>Add Snippet</span>
                </button>
              </div>
            </div>

            {/* Error banner */}
            {state === 'error' && error && (
              <div className="mb-4 flex shrink-0 items-start gap-3 rounded-xl border border-error-container bg-error-container/40 px-4 py-3">
                <Icon name="error" className="mt-0.5 text-error" />
                <div className="flex-1">
                  <p className="font-body-md text-body-md text-on-surface">{error}</p>
                  <button
                    type="button"
                    onClick={load}
                    className="mt-1 inline-flex min-h-11 items-center font-label-lg text-label-lg text-primary hover:underline"
                  >
                    Try again
                  </button>
                </div>
              </div>
            )}

            {/* Snippets Grid */}
            <div className="grid flex-1 grid-cols-1 content-start gap-3 overflow-y-auto pr-1 pb-8 auto-rows-max xl:grid-cols-2">
              {state === 'loading' && snippets.length === 0 && (
                <>
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div
                      key={`skel-${i}`}
                      className="h-36 animate-pulse rounded-xl bg-surface-container-low p-5"
                    >
                      <div className="mb-3 flex items-center gap-3">
                        <div className="h-7 w-20 rounded-lg bg-surface-container-high" />
                        <div className="h-5 w-40 rounded bg-surface-container-high" />
                      </div>
                      <div className="space-y-2">
                        <div className="h-3 w-full rounded bg-surface-container-high" />
                        <div className="h-3 w-5/6 rounded bg-surface-container-high" />
                        <div className="h-3 w-2/3 rounded bg-surface-container-high" />
                      </div>
                    </div>
                  ))}
                </>
              )}

              {state !== 'error' && snippets.length === 0 && state !== 'loading' && (
                <div className="col-span-full flex flex-col items-center justify-center rounded-xl bg-surface-container-low px-6 py-14 text-center">
                  <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-surface-container-high">
                    <Icon name="content_copy" className="text-on-surface-variant" />
                  </div>
                  <h3 className="font-headline-md text-headline-md text-on-surface">
                    No snippets yet
                  </h3>
                  <p className="mt-2 max-w-sm font-body-md text-body-md text-on-surface-variant">
                    Snippets expand a short trigger into full text anywhere you type. Add your
                    first one to start saving time.
                  </p>
                  <button
                    type="button"
                    onClick={focusCreate}
                    className="mt-6 flex h-11 items-center gap-2 rounded-lg bg-primary px-5 font-label-lg text-label-lg font-medium text-on-primary transition-colors hover:bg-primary/90 active:scale-95 active:duration-200"
                  >
                    <Icon name="add" className="text-[20px]" />
                    <span>Add your first snippet</span>
                  </button>
                </div>
              )}

              {state !== 'error' &&
                snippets.length > 0 &&
                filtered.length === 0 && (
                  <div className="col-span-full flex flex-col items-center justify-center rounded-xl bg-surface-container-low px-6 py-14 text-center">
                    <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-surface-container-high">
                      <Icon name="search_off" className="text-on-surface-variant" />
                    </div>
                    <h3 className="font-title-md text-title-md text-on-surface">
                      No matches for “{query}”
                    </h3>
                    <p className="mt-2 font-body-md text-body-md text-on-surface-variant">
                      Try a different trigger or phrase.
                    </p>
                  </div>
                )}

              {filtered.map((s) => (
                <article
                  key={s.snippet_id}
                  className="group relative flex min-h-36 flex-col rounded-xl bg-surface-container-low p-5 transition-colors hover:bg-surface-container"
                >
                  <div className="mb-3 flex items-start justify-between">
                    <button
                      type="button"
                      onClick={() => startEdit(s)}
                      aria-label={`Edit snippet ${s.trigger}`}
                      className="min-w-0 flex-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >
                      <span className="inline-flex max-w-full rounded-md bg-primary/10 px-2.5 py-1 font-mono text-sm font-medium tracking-wide text-primary">
                        <span className="truncate">{s.trigger}</span>
                      </span>
                      <h3 className="mt-3 truncate font-title-md text-title-md text-on-surface">
                        {snippetTitle(s)}
                      </h3>
                    </button>
                    <button
                      ref={(element) => {
                        if (element) menuTriggerRefs.current.set(s.snippet_id, element);
                        else menuTriggerRefs.current.delete(s.snippet_id);
                      }}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (menuId === s.snippet_id) closeMenu();
                        else setMenuId(s.snippet_id);
                      }}
                      className="-mr-2 -mt-2 flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-lg text-on-surface-variant opacity-0 transition-colors hover:bg-surface-container-highest hover:text-primary focus:opacity-100 group-hover:opacity-100"
                      aria-label="Snippet actions"
                      aria-controls={`snippet-actions-${s.snippet_id}`}
                      aria-expanded={menuId === s.snippet_id}
                    >
                      <Icon name="more_vert" />
                    </button>
                    {menuId === s.snippet_id && (
                      <>
                        <div
                          className="fixed inset-0 z-40"
                          onClick={(e) => {
                            e.stopPropagation();
                            closeMenu();
                          }}
                        />
                        <div
                          id={`snippet-actions-${s.snippet_id}`}
                          className="absolute right-0 top-10 z-50 w-40 rounded-lg bg-surface-container-high py-1 shadow-xl ring-1 ring-outline-variant/70"
                        >
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              startEdit(s);
                              restoreMenuFocus(s.snippet_id);
                            }}
                            className="flex min-h-11 w-full items-center gap-2 px-4 text-left font-body-md text-body-md text-on-surface transition-colors hover:bg-surface-container-highest"
                          >
                            <Icon name="edit" className="text-[18px]" />
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteTriggerRef.current =
                                menuTriggerRefs.current.get(s.snippet_id) ?? null;
                              setMenuId(null);
                              setDeleteId(s.snippet_id);
                            }}
                            className="flex min-h-11 w-full items-center gap-2 px-4 py-2 text-left font-body-md text-body-md text-error transition-colors hover:bg-surface-container-highest"
                          >
                            <Icon name="delete" className="text-[18px]" />
                            Delete
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                  <p className="mt-auto line-clamp-3 whitespace-pre-wrap break-words font-body-md text-body-md text-on-surface-variant">
                    {s.expansion}
                  </p>
                </article>
              ))}
            </div>
          </div>

          {/* Add/Edit Side Panel */}
          <aside className="flex w-full shrink-0 flex-col overflow-hidden rounded-xl bg-surface-container-low/60 ring-1 ring-outline-variant/40 lg:h-full lg:w-[344px]">
            <div className="flex shrink-0 items-center justify-between border-b border-outline-variant/40 px-5 py-4">
              <div>
                <p className="font-label-sm text-label-sm uppercase tracking-[0.12em] text-on-surface-variant">
                  Editor
                </p>
                <h3 className="mt-1 flex items-center gap-2 font-headline-md text-headline-md text-on-surface">
                  <Icon name={isEditing ? 'edit_document' : 'add'} className="text-primary" />
                  {isEditing ? 'Edit snippet' : 'Add snippet'}
                </h3>
              </div>
              <button
                type="button"
                onClick={isEditing ? cancelEdit : focusCreate}
                className="flex min-h-11 min-w-11 items-center justify-center rounded-lg text-on-surface-variant transition-colors hover:bg-surface-container-highest hover:text-on-surface"
                aria-label="Close editor"
              >
                <Icon name="close" />
              </button>
            </div>

            {isEditing && editingSnippet ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  saveEdit(editingSnippet);
                }}
                className="flex min-h-0 flex-1 flex-col"
              >
                <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
                  <div className="space-y-2">
                    <label
                      htmlFor="edit-snippet-trigger"
                      className="block font-label-lg text-label-lg text-on-surface-variant"
                    >
                      Shortcut trigger
                    </label>
                    <input
                      id="edit-snippet-trigger"
                      type="text"
                      value={editForm.trigger}
                      onChange={(e) => setEditForm({ ...editForm, trigger: e.target.value })}
                      maxLength={MAX_TRIGGER_LENGTH}
                      placeholder="e.g. ;sig"
                      className={`${INPUT_BASE} h-11 font-medium tracking-wide text-primary`}
                    />
                    <p className="mt-1 font-label-sm text-label-sm text-on-surface-variant">
                      Typing this exact sequence will trigger the expansion.
                    </p>
                    {editErrors.trigger && (
                      <p className="mt-1 font-label-sm text-label-sm text-error">
                        {editErrors.trigger}
                      </p>
                    )}
                    <p className="mt-1 font-label-sm text-label-sm text-on-surface-variant">
                      {editForm.trigger.length}/{MAX_TRIGGER_LENGTH}
                    </p>
                  </div>
                  <div className="flex flex-1 flex-col space-y-2">
                    <label
                      htmlFor="edit-snippet-content"
                      className="block font-label-lg text-label-lg text-on-surface-variant"
                    >
                      Snippet content
                    </label>
                    <textarea
                      id="edit-snippet-content"
                      value={editForm.expansion}
                      onChange={(e) => setEditForm({ ...editForm, expansion: e.target.value })}
                      maxLength={MAX_EXPANSION_LENGTH}
                      placeholder="Text that replaces the trigger…"
                      className="w-full flex-1 resize-none rounded-lg border border-outline-variant/60 bg-surface p-3.5 font-body-md text-body-md text-on-surface transition-colors placeholder:text-on-surface-variant focus:border-primary focus:outline-none focus:ring-0"
                      rows={8}
                    />
                    {editErrors.expansion && (
                      <p className="mt-1 font-label-sm text-label-sm text-error">
                        {editErrors.expansion}
                      </p>
                    )}
                    <p className="mt-1 font-label-sm text-label-sm text-on-surface-variant">
                      {editForm.expansion.length}/{MAX_EXPANSION_LENGTH}
                    </p>
                  </div>
                </div>
                <div className="mt-auto flex shrink-0 justify-end gap-2 border-t border-outline-variant/40 px-5 py-4">
                  <button
                    type="button"
                    onClick={cancelEdit}
                    disabled={saving}
                    className="h-11 rounded-lg border border-outline-variant px-4 font-label-lg text-label-lg font-medium text-on-surface transition-colors hover:bg-surface-container-highest active:scale-95 active:duration-200 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="h-11 rounded-lg bg-primary px-5 font-label-lg text-label-lg font-medium text-on-primary transition-colors hover:bg-primary/90 active:scale-95 active:duration-200 disabled:opacity-50"
                  >
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleCreate} className="flex min-h-0 flex-1 flex-col">
                <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
                  <div className="space-y-2">
                    <label
                      htmlFor="create-snippet-trigger"
                      className="block font-label-lg text-label-lg text-on-surface-variant"
                    >
                      Shortcut trigger
                    </label>
                    <input
                      id="create-snippet-trigger"
                      ref={createTriggerRef}
                      type="text"
                      value={createForm.trigger}
                      onChange={(e) => setCreateForm({ ...createForm, trigger: e.target.value })}
                      maxLength={MAX_TRIGGER_LENGTH}
                      placeholder="e.g. ;sig"
                      className={`${INPUT_BASE} h-11 font-medium tracking-wide text-primary`}
                    />
                    <p className="mt-1 font-label-sm text-label-sm text-on-surface-variant">
                      Typing this exact sequence will trigger the expansion.
                    </p>
                    {createErrors.trigger && (
                      <p className="mt-1 font-label-sm text-label-sm text-error">
                        {createErrors.trigger}
                      </p>
                    )}
                    <p className="mt-1 font-label-sm text-label-sm text-on-surface-variant">
                      {createForm.trigger.length}/{MAX_TRIGGER_LENGTH}
                    </p>
                  </div>
                  <div className="flex flex-1 flex-col space-y-2">
                    <label
                      htmlFor="create-snippet-content"
                      className="block font-label-lg text-label-lg text-on-surface-variant"
                    >
                      Snippet content
                    </label>
                    <textarea
                      id="create-snippet-content"
                      value={createForm.expansion}
                      onChange={(e) =>
                        setCreateForm({ ...createForm, expansion: e.target.value })
                      }
                      maxLength={MAX_EXPANSION_LENGTH}
                      placeholder="Text that replaces the trigger…"
                      className="w-full flex-1 resize-none rounded-lg border border-outline-variant/60 bg-surface p-3.5 font-body-md text-body-md text-on-surface transition-colors placeholder:text-on-surface-variant focus:border-primary focus:outline-none focus:ring-0"
                      rows={8}
                    />
                    {createErrors.expansion && (
                      <p className="mt-1 font-label-sm text-label-sm text-error">
                        {createErrors.expansion}
                      </p>
                    )}
                    <p className="mt-1 font-label-sm text-label-sm text-on-surface-variant">
                      {createForm.expansion.length}/{MAX_EXPANSION_LENGTH}
                    </p>
                  </div>
                </div>
                <div className="mt-auto flex shrink-0 justify-end gap-2 border-t border-outline-variant/40 px-5 py-4">
                  <button
                    type="button"
                    onClick={focusCreate}
                    disabled={saving}
                    className="h-11 rounded-lg border border-outline-variant px-4 font-label-lg text-label-lg font-medium text-on-surface transition-colors hover:bg-surface-container-highest active:scale-95 active:duration-200 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="h-11 rounded-lg bg-primary px-5 font-label-lg text-label-lg font-medium text-on-primary transition-colors hover:bg-primary/90 active:scale-95 active:duration-200 disabled:opacity-50"
                  >
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </form>
            )}
          </aside>
        </div>
      </div>

      {/* Delete confirmation */}
      {deleteId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => !saving && setDeleteId(null)}
        >
          <div
            ref={deleteDialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="snippet-delete-dialog-title"
            aria-describedby="snippet-delete-dialog-description"
            tabIndex={-1}
            className="w-full max-w-sm rounded-2xl border border-surface-container-highest bg-surface-container-low p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-error-container">
                <Icon name="delete" className="text-on-error-container" />
              </div>
              <h3
                id="snippet-delete-dialog-title"
                className="font-headline-md text-headline-md text-on-surface"
              >
                Delete snippet?
              </h3>
            </div>
            <p
              id="snippet-delete-dialog-description"
              className="font-body-md text-body-md text-on-surface-variant"
            >
              This removes the snippet from your keyboard. The deletion syncs to your other
              devices.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                ref={deleteCancelRef}
                type="button"
                onClick={() => setDeleteId(null)}
                disabled={saving}
                className="min-h-11 rounded-xl border border-outline-variant px-6 font-label-lg text-label-lg font-medium text-on-surface transition-colors hover:bg-surface-container-highest active:scale-95 active:duration-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={saving}
                className="min-h-11 rounded-xl bg-error-container px-6 font-label-lg text-label-lg font-medium text-on-error-container transition-colors hover:opacity-90 active:scale-95 active:duration-200 disabled:opacity-50"
              >
                {saving ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
